/**
 * @file Meet.tsx
 * @description Main page for launching a meeting and viewing meeting history.
 * This component renders buttons for creating a new meeting and navigating to the list manager,
 * displays the meeting history, and uses the CreateMeetingModal for meeting creation.
 */

import React, { useState } from "react";
import { Box, Button, Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MeetingHistory from "./MeetingHistory";
import ScheduledMeetings from "./ScheduledMeetings";
import { ROUTE_MAIL_LIST } from "constants/routerPath";
import CreateMeetingModal from "./CreateMeetingModal";

const Meet: React.FC = () => {
  const navigate = useNavigate();
  // State to control the visibility of the CreateMeetingModal.
  const [openModal, setOpenModal] = useState<boolean>(false);
  // State to trigger refresh of scheduled meetings
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  /**
   * Navigates to the Mail List Manager screen.
   */
  const handleNavigateToListManager = () => {
    navigate(ROUTE_MAIL_LIST);
  };

  /**
   * Triggers a refresh of the scheduled meetings table.
   */
  const handleRefreshScheduledMeetings = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <Container>
      <Box>
        {/* Button to open the CreateMeetingModal */}
        <Button
          variant="contained"
          data-tour="canmeet-create-btn"
          onClick={() => setOpenModal(true)}
          sx={{ mr: 3, mb: 3 }}
        >
          + Create Meeting
        </Button>
        {/* Button to navigate to the Mail List Manager */}
        <Button
          variant="outlined"
          onClick={handleNavigateToListManager}
          sx={{ mr: 3, mb: 3 }}
        >
          List Manager
        </Button>
      </Box>
      {/* Render scheduled meetings */}
      <ScheduledMeetings refreshTrigger={refreshTrigger} />
      {/* Render meeting history */}
      <MeetingHistory />
      {/* Render the meeting creation modal */}
      <CreateMeetingModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onNavigateToListManager={handleNavigateToListManager}
        onMeetingScheduled={handleRefreshScheduledMeetings}
      />
    </Container>
  );
};

export default Meet;
